import { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { FaCheck, FaCircle, FaTimes, FaTrash } from "react-icons/fa";
import { LuCopy, LuCopyCheck } from "react-icons/lu";
import eventsAPI from "../../../api/eventsAPI";
import { toast } from "react-toastify";
import { useEvent } from "../../../context/EventContext";

// * The Connections component renders a list of connection-related messages
// * for the current user. It provides options to view, accept, reject, or
// * delete individual connection requests. The component also allows for
// * batch operations such as selecting multiple requests and deleting them
// * together. It includes functionalities to mark messages as read when
// * viewed and updates the message status in the backend.
const Connections = () => {
  const { user, setUser } = useAuth();
  const { setLoading, loading } = useEvent();
  const [selectedMessage, setSelectedMessage] = useState(null); // State to track the selected message
  const [showCheckboxes, setShowCheckboxes] = useState(false); // Toggle to show checkboxes
  const [selectedMessages, setSelectedMessages] = useState([]); // Track selected messages

  // Check if user exists and messages are available
  if (!user || !user.messages || user.messages.length === 0) {
    return <p>No messages found.</p>; // Fallback message if no messages exist
  }

  // Filter messages by type "Connection"
  const connectionMessages = user.messages.filter(
    (message) => message.Type === "Connection"
  );

  //   * Handles the "View" button click for a message.
  //   * If the message is already marked as "Read", it will set the selected message
  //   * without making the API call. If the message is not marked as "Read", it will
  //   * update the message's read status in the backend and then update the message
  //   * read status in the UI.
  const handleViewClick = async (message) => {
    if (message.Read === true) {
      setSelectedMessage(message);
      return;
    }

    try {
      const response = await eventsAPI.post("/profile/updateStatus", {
        messageID: message._id,
      });

      if (response.status === 200) {
        const updatedMessages = user.messages.map((msg) =>
          msg._id === message._id ? { ...msg, Read: true } : msg
        );

        setUser({ ...user, messages: updatedMessages });
        setSelectedMessage(message);
      }
    } catch (error) {
      console.error("Failed to mark message as read", error);
    }
  };

  // Handle the "Back" button click
  const handleBackClick = () => {
    setSelectedMessage(null); // Reset the selected message to go back to the list
  };

  // Toggle showing checkboxes
  const toggleCheckboxes = () => {
    setShowCheckboxes(!showCheckboxes);
    setSelectedMessages([]); // Reset selected messages when toggling
  };

  // Handle individual checkbox click
  const handleCheckboxChange = (id) => {
    if (selectedMessages.includes(id)) {
      setSelectedMessages(
        selectedMessages.filter((messageId) => messageId !== id)
      );
    } else {
      setSelectedMessages([...selectedMessages, id]);
    }
  };

  // Handle select all checkboxes
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allMessageIds = connectionMessages.map((msg) => msg._id);
      setSelectedMessages(allMessageIds);
    } else {
      setSelectedMessages([]);
    }
  };

  // Handle delete for individual messages
  const handleDelete = async (e, id) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.delete(
        `/profile/deleteInvitation/${id}`
      );

      if (response.status === 200) {
        toast.success("Message deleted successfully!");

        const updatedMessages = user.messages.filter((msg) => msg._id !== id);
        setUser({ ...user, messages: updatedMessages });
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete the message");
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting all selected messages
  const handleDeleteSelectedAll = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (selectedMessages.length === 0) {
      toast.error("No messages selected for deletion.");
      setLoading(false);
      return;
    }

    try {
      const response = await eventsAPI.delete(`/profile/deleteInvitation`, {
        data: { messageIDs: selectedMessages },
      });

      if (response.status === 200) {
        toast.success("Messages deleted successfully!");

        const updatedMessages = user.messages.filter(
          (msg) => !selectedMessages.includes(msg._id)
        );
        setUser({ ...user, messages: updatedMessages });
        setSelectedMessages([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete the selected messages");
    } finally {
      setLoading(false);
    }
  };

  //   * Handles the response to a connection invitation by sending a POST request to the /profile/acceptRequest endpoint.
  //   * If successful, it displays a success message, updates the UI, and resets the selected message.
  //   * If the request fails, it shows an error message. The UI is updated to show the loading state while the request is being processed.
  const handleResponse = async (e, responseType, messageID) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.post("/profile/acceptRequest", {
        messageID,
        responseType,
      });

      if (response.status === 200) {
        if (responseType === "Accept") {
          toast.success("Invitation Accepted");
          setLoading(false);
        } else {
          toast.success("Invitation Rejected");
          setLoading(false);
        }
        setSelectedMessage(null);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      toast.error("An error occurred while processing the request.");
    }
  };

  return (
    <div className="message-section">
      {/* Toggle button to show/hide checkboxes */}
      <button className="add-space" onClick={toggleCheckboxes}>
        {showCheckboxes ? <LuCopyCheck /> : <LuCopy />}
      </button>

      {/* Show Select All checkbox and Delete button if checkboxes are visible */}
      {showCheckboxes && (
        <div className="selection-controls">
          <div className="checkbox-top">
            <input
              type="checkbox"
              onChange={handleSelectAll}
              className="checkbox"
              checked={
                selectedMessages.length === connectionMessages.length &&
                connectionMessages.length > 0
              }
            />
            <span>Select All</span>
          </div>

          {/* Delete button enabled only when messages are selected */}
          <button
            className="add-space"
            onClick={handleDeleteSelectedAll}
            disabled={selectedMessages.length === 0 || loading}
          >
            <FaTrash />
          </button>
        </div>
      )}

      {selectedMessage ? (
        <div className="message-details">
          {/* Conditional rendering of status based on message type */}
          {selectedMessage.content === "Connection Request Accepted" ? (
            <span
              style={{ color: "green", fontWeight: "bold", fontSize: "20px" }}
            >
              Connection Request Accepted
            </span>
          ) : selectedMessage.content === "Rejected" ? (
            <span
              style={{ color: "red", fontWeight: "bold", fontSize: "20px" }}
            >
              Connection Request Rejected
            </span>
          ) : (
            <div className="request-buttons">
              {/* Only show Accept and Reject buttons */}
              <button
                className="accept"
                onClick={(e) =>
                  handleResponse(e, "Accept", selectedMessage._id)
                }
              >
                {loading ? <FaCheck /> : "Accept"}
              </button>
              <button
                className="reject"
                onClick={(e) =>
                  handleResponse(e, "Reject", selectedMessage._id)
                }
              >
                {loading ? <FaTimes /> : "Reject"}
              </button>
            </div>
          )}

          {/* Back button */}
          <button className="add-space" onClick={handleBackClick}>
            Back to Connections
          </button>
        </div>
      ) : (
        <div>
          {/* Map through the filtered connection messages array and render each message */}
          {connectionMessages.length === 0 ? (
            <p>No connection requests found.</p>
          ) : (
            connectionMessages.map((message, index) => (
              <div key={index} className="message-card">
                {/* Checkbox for each message */}
                {showCheckboxes && (
                  <input
                    type="checkbox"
                    className="checkbox"
                    onChange={() => handleCheckboxChange(message._id)}
                    checked={selectedMessages.includes(message._id)}
                  />
                )}

                <p>
                  {message.From
                    ? `${message.From.firstName} ${message.From.lastName} sent you a Connection Request`
                    : "Unknown Sender"}
                </p>
                <div className="status">
                  {message.Read === true ? (
                    <>
                      <FaCircle
                        style={{
                          color: "rgb(60, 249, 60)",
                          height: "10px",
                          width: "10px",
                        }}
                      />
                      <p>Read</p>
                    </>
                  ) : (
                    <>
                      <FaCircle
                        style={{
                          color: "rgb(255, 0, 0)",
                          height: "10px",
                          width: "10px",
                        }}
                      />
                      <p>Unread</p>
                    </>
                  )}
                </div>

                {/* View and Delete buttons */}
                <div className="request-button">
                  <button
                    className="add-space"
                    onClick={() => handleViewClick(message)}
                  >
                    View
                  </button>
                  <button
                    className="add-space"
                    onClick={(e) => handleDelete(e, message._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Connections;
